// Global Instructions Rule Applied!
//
// The bulk review of highlights the agent REWORDED (owner 2026-11-05: the
// texts the finalise had clipped with "…" and the investment introductions
// that restated their heading are written again by the agent, and reviewed
// here rather than one set form at a time), on Configuration → Follower
// Agents → Highlight rewording. One antd Table per tab (To sign off / Signed
// off) with a checkbox column; the action row above it carries the selection
// count, Clear, and the bulk actions behind a Popconfirm that names the count
// (the bulk-action bar grammar, the company short descriptions' sibling).
// Each row shows the set, the highlight and every text the rewording changed
// as before → after, and carries the same verbs: Sign off (the ask, primary),
// Edit and Regenerate (bordered). Edit opens an AntD modal on the raw texts
// (figures as {tokens}, as the set form edits them); a saved edit makes the
// text the person's, so it leaves this list. Regenerate queues one rewording
// run per set; the list shows the new text once the run has landed.
//
// Signing off renews the SET's sign-off where the rewording was its only
// change since; a set nobody had signed off stays "not reviewed" in the
// reports (the row says so) — that sign-off is the set form's.
import React, { useCallback, useMemo, useState } from 'react'
import { Button, Input, Modal, Popconfirm, Table, Tag, Tooltip, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import dayjs from 'dayjs'
import { toast } from 'react-toastify'
import { showErrorToast } from '../../../core/utils/errorToast.tsx'
import { getAccessToken } from '../../../core/auth/authService'
import { store } from '../../../state_manager/store.ts'
import {
  editHighlightItem,
  regenerateRewordedHighlightItems,
  signOffRewordedHighlightItems
} from '../../period_highlights/services/periodHighlightsService.ts'
import type { RewordedHighlightItem, RewordedHighlightItems } from '../../../state_manager/data_models/period_highlights_model.ts'

const { Text } = Typography

const RECORD_LINK_CLASS =
  'cursor-pointer text-blue-600 hover:text-blue-700 hover:underline bg-transparent border-0 p-0 text-left'
const TABLE_SCROLL = { y: 460 }
const DATE_FORMAT = 'MM/DD/YYYY'
const SCOPE_LABEL: Record<string, string> = { fund: 'Fund', fund_group: 'Consolidated fund', lp_account: 'LP' }
const FIELDS = [
  { key: 'headline', label: 'Headline' },
  { key: 'body', label: 'Body' },
  { key: 'short_text', label: 'Short text' }
] as const
type FieldKey = (typeof FIELDS)[number]['key']

const token = () => getAccessToken(store.getState().auth)
const errorText = (error: unknown, fallback: string) => (error instanceof Error ? error.message : fallback)

const resolved = (row: RewordedHighlightItem, field: FieldKey, before: boolean): string | null => {
  switch (field) {
    case 'headline':
      return before ? row.prog__before_headline_resolved : row.prog__headline_resolved
    case 'body':
      return before ? row.prog__before_body_resolved : row.prog__body_resolved
    default:
      return before ? row.prog__before_short_text_resolved : row.prog__short_text_resolved
  }
}

/** The fields the rewording changed, before → after (resolved for reading). */
export const changedFields = (row: RewordedHighlightItem) =>
  FIELDS.filter((f) => (resolved(row, f.key, true) ?? '') !== (resolved(row, f.key, false) ?? '')).map((f) => ({
    ...f,
    before: resolved(row, f.key, true),
    after: resolved(row, f.key, false)
  }))

interface HighlightRewordingReviewTableProps {
  rows: RewordedHighlightItem[]
  caps: RewordedHighlightItems['caps']
  /** Opens the highlight set form. */
  openSet: (row: RewordedHighlightItem) => void
  /** Called after any change, to reload the list. */
  onChanged: () => void | Promise<void>
  empty: string
}

export function HighlightRewordingReviewTable({ rows, caps, openSet, onChanged, empty }: HighlightRewordingReviewTableProps) {
  const [selected, setSelected] = useState<string[]>([])
  const [signingOff, setSigningOff] = useState(false)
  const [regenerating, setRegenerating] = useState(false)
  const [editing, setEditing] = useState<RewordedHighlightItem | null>(null)
  const [draft, setDraft] = useState<Record<FieldKey, string>>({ headline: '', body: '', short_text: '' })
  const [saving, setSaving] = useState(false)

  const busy = signingOff || regenerating
  const signableIds = useMemo(
    () => new Set(rows.filter((row) => row.prog__status === 'unreviewed').map((row) => row.highlight_items__id)),
    [rows]
  )
  // a row that left the list (signed off on another tab, edited) leaves the selection too
  const selectedHere = useMemo(
    () => selected.filter((id) => rows.some((row) => row.highlight_items__id === id)),
    [selected, rows]
  )
  const selectedToSignOff = selectedHere.filter((id) => signableIds.has(id))

  const signOff = useCallback(
    async (itemIds: string[]) => {
      setSigningOff(true)
      try {
        const result = await signOffRewordedHighlightItems(itemIds, token())
        const renewed = result.sets_renewed > 0 ? `; ${result.sets_renewed} set${result.sets_renewed === 1 ? '' : 's'} signed off again` : ''
        toast.success(
          (result.signed_off === 1 ? '1 reworded highlight signed off' : `${result.signed_off} reworded highlights signed off`) + renewed
        )
        setSelected((prev) => prev.filter((id) => !itemIds.includes(id)))
        await onChanged()
      } catch (error) {
        showErrorToast(errorText(error, 'The rewordings could not be signed off.'))
      } finally {
        setSigningOff(false)
      }
    },
    [onChanged]
  )

  const regenerate = useCallback(
    async (itemIds: string[]) => {
      setRegenerating(true)
      try {
        const result = await regenerateRewordedHighlightItems(itemIds, token())
        const failed = result.results.filter((r) => r.status === 'error')
        if (result.enqueued > 0) {
          toast.success(
            `${result.enqueued} rewording run${result.enqueued === 1 ? '' : 's'} queued — the new text shows here once the agent has written it`
          )
        }
        if (failed.length > 0) {
          showErrorToast(`Not queued: ${failed.map((r) => `${r.name || 'a set'} · ${r.period_label || r.period_end}`).join(', ')}. ${failed[0].error || ''}`)
        }
        setSelected((prev) => prev.filter((id) => !itemIds.includes(id)))
        await onChanged()
      } catch (error) {
        showErrorToast(errorText(error, 'The rewording could not be queued.'))
      } finally {
        setRegenerating(false)
      }
    },
    [onChanged]
  )

  const openEdit = (row: RewordedHighlightItem) => {
    setEditing(row)
    setDraft({
      headline: row.highlight_items__headline,
      body: row.highlight_items__body ?? '',
      short_text: row.highlight_items__short_text ?? ''
    })
  }

  const draftOver = (field: FieldKey) => draft[field].trim().length > caps[field]
  const draftInvalid = !draft.headline.trim() || FIELDS.some((f) => draftOver(f.key))

  const saveEdit = useCallback(async () => {
    if (!editing) return
    setSaving(true)
    try {
      const result = await editHighlightItem(
        editing.highlight_items__id,
        { headline: draft.headline.trim(), body: draft.body.trim() || null, short_text: draft.short_text.trim() || null },
        token()
      )
      toast.success('Highlight saved')
      if (result.warnings?.length) toast.warning(result.warnings.join(' '))
      setEditing(null)
      await onChanged()
    } catch (error) {
      showErrorToast(errorText(error, 'The highlight could not be saved.'))
    } finally {
      setSaving(false)
    }
  }, [editing, draft, onChanged])

  const columns: ColumnsType<RewordedHighlightItem> = [
    {
      title: 'Set',
      key: 'set',
      width: 210,
      render: (_: unknown, row) => (
        <div>
          <button type='button' className={RECORD_LINK_CLASS} onClick={() => openSet(row)}>
            {row.prog__scope_name || SCOPE_LABEL[row.highlight_sets__scope_type] || 'Set'} · {row.prog__period_label || row.highlight_sets__period_end}
          </button>
          <div className='text-xs text-gray-400'>
            {SCOPE_LABEL[row.highlight_sets__scope_type] || row.highlight_sets__scope_type}
            {row.highlight_sets__status === 'draft' ? ' · draft' : ''}
          </div>
          {row.prog__set_review_status === 'unreviewed' ? (
            <Tooltip title='Nobody has signed off this set. Signing off its reworded lines does not review the rest: open the set, read it and press Sign off there.'>
              <Tag color='gold' className='!mr-0 mt-1'>
                Set not reviewed
              </Tag>
            </Tooltip>
          ) : null}
        </div>
      )
    },
    {
      title: 'Highlight',
      key: 'highlight',
      width: 200,
      render: (_: unknown, row) => (
        <div>
          <div>{row.prog__source_account_name || row.prog__scope_name || '—'}</div>
          <div className='text-xs text-gray-400'>{row.highlight_categories__label || row.highlight_categories__category_key}</div>
          {row.prog__pinned ? (
            <Tooltip title='Printed in full under the New Investments heading of the quarterly report.'>
              <Tag color='geekblue' className='!mr-0 mt-1'>
                New investment
              </Tag>
            </Tooltip>
          ) : null}
        </div>
      )
    },
    {
      title: 'Before → after',
      key: 'texts',
      render: (_: unknown, row) => {
        const changed = changedFields(row)
        if (changed.length === 0) return <Text type='secondary'>Nothing changed in the printed text.</Text>
        return (
          <div className='space-y-2'>
            {changed.map((f) => (
              <div key={f.key}>
                <div className='text-[10.5px] tracking-wider uppercase text-gray-400'>{f.label}</div>
                <div className='text-gray-400 line-through decoration-gray-300'>{f.before || '—'}</div>
                <div>{f.after || '—'}</div>
              </div>
            ))}
          </div>
        )
      }
    },
    {
      title: 'Reworded',
      key: 'reworded',
      width: 100,
      render: (_: unknown, row) => {
        const on = dayjs(row.highlight_items__reworded_at).format(DATE_FORMAT)
        if (row.prog__status !== 'signed_off') return on
        const by = row.prog__reviewed_by_name ? ` by ${row.prog__reviewed_by_name}` : ''
        const at = row.highlight_items__reworded_reviewed_at
          ? ` on ${dayjs(row.highlight_items__reworded_reviewed_at).format(DATE_FORMAT)}`
          : ''
        return <Tooltip title={`Signed off${by}${at}.`}>{on}</Tooltip>
      }
    },
    {
      title: '',
      key: 'actions',
      width: 230,
      render: (_: unknown, row) => (
        <div className='flex gap-1 justify-end'>
          {row.prog__status === 'unreviewed' ? (
            <Button size='small' type='primary' disabled={busy} onClick={() => void signOff([row.highlight_items__id])}>
              Sign off
            </Button>
          ) : null}
          <Button size='small' disabled={busy} onClick={() => openEdit(row)}>
            Edit
          </Button>
          <Popconfirm
            title='Write this highlight again?'
            description='The agent rewords it once more from the same facts; the new text replaces this one, unreviewed.'
            okText='Regenerate'
            onConfirm={() => void regenerate([row.highlight_items__id])}
            disabled={busy}
          >
            <Button size='small' disabled={busy}>
              Regenerate
            </Button>
          </Popconfirm>
        </div>
      )
    }
  ]

  if (rows.length === 0) return <Text type='secondary'>{empty}</Text>

  return (
    <div className='space-y-2'>
      <div className='flex items-center gap-2 flex-wrap min-h-[24px]'>
        {selectedHere.length > 0 ? (
          <>
            <Text className='text-xs'>{selectedHere.length} selected</Text>
            <button type='button' className={`${RECORD_LINK_CLASS} text-xs`} onClick={() => setSelected([])}>
              Clear
            </button>
            {selectedToSignOff.length > 0 ? (
              <Popconfirm
                title={`Sign off ${selectedToSignOff.length} reworded highlight${selectedToSignOff.length === 1 ? '' : 's'}?`}
                description='A set whose only change since its sign-off is this rewording is signed off again.'
                okText='Sign off'
                onConfirm={() => void signOff(selectedToSignOff)}
                disabled={busy}
              >
                <Button size='small' type='primary' loading={signingOff} disabled={busy}>
                  Sign off selected
                </Button>
              </Popconfirm>
            ) : null}
            <Popconfirm
              title={`Write ${selectedHere.length} highlight${selectedHere.length === 1 ? '' : 's'} again?`}
              description='The agent rewords them once more, one run per set; each new text replaces the old one, unreviewed.'
              okText='Regenerate'
              onConfirm={() => void regenerate(selectedHere)}
              disabled={busy}
            >
              <Button size='small' loading={regenerating} disabled={busy}>
                Regenerate selected
              </Button>
            </Popconfirm>
          </>
        ) : (
          <Text type='secondary' className='text-xs'>
            Tick highlights to sign off or regenerate several at once.
          </Text>
        )}
      </div>
      <Table<RewordedHighlightItem>
        size='small'
        pagination={false}
        rowKey='highlight_items__id'
        columns={columns}
        dataSource={rows}
        scroll={TABLE_SCROLL}
        tableLayout='auto'
        rowSelection={{
          selectedRowKeys: selectedHere,
          onChange: (keys) => setSelected(keys.map(String)),
          getCheckboxProps: () => ({ disabled: busy })
        }}
      />
      <Modal
        open={editing != null}
        title={`Highlight: ${editing?.prog__source_account_name || editing?.prog__scope_name || ''} · ${editing?.prog__period_label || ''}`}
        okText='Save'
        okButtonProps={{ disabled: draftInvalid, loading: saving }}
        onOk={() => void saveEdit()}
        onCancel={() => setEditing(null)}
        destroyOnHidden
      >
        <div className='space-y-3'>
          {FIELDS.map((f) => (
            <div key={f.key}>
              <div className='text-gray-600 mb-1'>
                {f.label}{' '}
                <span className='text-gray-400'>
                  ({draft[f.key].trim().length}/{caps[f.key]})
                </span>
              </div>
              {f.key === 'headline' ? (
                <Input
                  value={draft.headline}
                  status={draftOver('headline') ? 'error' : undefined}
                  onChange={(e) => setDraft({ ...draft, headline: e.target.value })}
                />
              ) : (
                <Input.TextArea
                  value={draft[f.key]}
                  rows={f.key === 'body' ? 5 : 3}
                  status={draftOver(f.key) ? 'error' : undefined}
                  onChange={(e) => setDraft({ ...draft, [f.key]: e.target.value })}
                />
              )}
            </div>
          ))}
          <Text type='secondary' className='text-xs block'>
            Figures stay as {'{tokens}'} and print from the books. Saving makes it your own text: it leaves this list and the
            set reads "edited since sign-off" until you sign it off on the set form.
          </Text>
        </div>
      </Modal>
    </div>
  )
}

export default HighlightRewordingReviewTable
